import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import { Field } from '../ui';
import { changePassword as cognitoChangePassword } from '../../cognito';

// Cognito's pool policy is 12+ chars with mixed case, digits and symbols. The
// SDK enforces it server-side; this only catches the obvious typos before the
// round trip, so the message the admin sees is the one they can act on.
const MIN_LENGTH = 12;

export default function ChangePasswordModal({ open, onClose }) {
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setOldPw(''); setNewPw(''); setConfirmPw(''); setError(''); setSaving(false);
  };

  const close = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPw !== confirmPw) { setError('New passwords do not match'); return; }
    if (newPw.length < MIN_LENGTH) { setError(`Password must be at least ${MIN_LENGTH} characters`); return; }
    setSaving(true);
    const result = await cognitoChangePassword(oldPw, newPw);
    setSaving(false);
    if (result.status === 'success') {
      toast.success('Password updated');
      reset();
      onClose();
    } else {
      setError(result.message || 'Failed to change password');
    }
  };

  const tooShort = newPw.length > 0 && newPw.length < MIN_LENGTH;
  const mismatch = confirmPw.length > 0 && confirmPw !== newPw;

  return (
    <Modal open={open} onClose={close} title="Change password" maxWidth={420}>
      <form onSubmit={handleSubmit} noValidate>
        {error && (
          <div className="notice notice-danger" role="alert">{error}</div>
        )}
        <Field label="Current password" htmlFor="cp-old">
          <input id="cp-old" type="password" className="form-input" autoComplete="current-password"
                 value={oldPw} onChange={(e) => setOldPw(e.target.value)} required />
        </Field>
        <Field label="New password" htmlFor="cp-new"
               hint={`At least ${MIN_LENGTH} characters, with upper and lower case, a digit and a symbol.`}
               error={tooShort ? `${MIN_LENGTH - newPw.length} more characters needed` : null}>
          <input id="cp-new" type="password" className="form-input" autoComplete="new-password"
                 value={newPw} onChange={(e) => setNewPw(e.target.value)} aria-invalid={tooShort || undefined} required />
        </Field>
        <Field label="Confirm new password" htmlFor="cp-confirm"
               error={mismatch ? 'Does not match the new password' : null}>
          <input id="cp-confirm" type="password" className="form-input" autoComplete="new-password"
                 value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} aria-invalid={mismatch || undefined} required />
        </Field>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={close} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving || !oldPw || !newPw || !confirmPw}>
            {saving ? <><span className="spinner-sm" /> Saving…</> : 'Update password'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
